import React, { useState, useMemo, useRef, useEffect } from "react";
import operadorLogo from "../assets/images/operadorTelecomLogo.png";
import robotImg from "../assets/images/robot_7626611.png";
import geminiBotIcon from "../assets/images/gemini-bot.svg";
import geminiUserIcon from "../assets/images/gemini-user.svg";
import mic from "../assets/images/mic2.png";
import settings from "../assets/images/setting_2040504.png";
import attach from "../assets/images/attach.png";
import add from "../assets/images/add.png";
import send from "../assets/images/send.png";
import userProfile from "../assets/images/social-media_16327810.png";
import styles from "../styles/AIChat.module.scss";
import { ChatService, ChatMessage } from "../services/AIChatService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Chart from "react-apexcharts";
import trash from "../assets/images/trash_12616431.png";
import "../styles/chat.css";

type chatProps = {
	checkWhenLogged: (logged: boolean) => void;
};

// Función para limpiar caracteres problemáticos y normalizar encoding
const cleanMessageText = (text: string): string => {
	if (!text) return text;
	// Normalizar caracteres Unicode (NFD descompone caracteres acentuados)
	// Luego recomponerlos (NFC) para estandarizar
	let cleaned = text.normalize("NFC");

	// Reemplazar caracteres problemáticos/corruptos por su equivalente correcto
	cleaned = cleaned
		.replace(/\u0092/g, "'") // Comilla simple problemática
		.replace(/[\u0080-\u009F]/g, " ") // Reemplazar caracteres de control por espacio
		.replace(/[^\x20-\x7E\u00C0-\u00FF\n\r.,:;!?()\-]/g, " "); // Mantener caracteres válidos

	return cleaned;
};

const AiChat = ({ checkWhenLogged }: chatProps) => {
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	const [userMessage, setUserMessage] = useState("");
	const chatService = useMemo(() => new ChatService(), []);
	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const [isAiTyping, setIsAiTyping] = useState(false);

	useEffect(() => {
		const unsubscribe = chatService.subscribe((updatedMessages) => {
			setChatMessages([...updatedMessages]); // Update UI whenever a new message arrives
			setIsAiTyping(chatService.isTyping);
		});
		return () => unsubscribe();
	}, [chatService]);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [chatMessages, isAiTyping]);

	const sendMessage = (text?: string) => {
		const messageToSend = text || userMessage;
		if (!messageToSend.trim()) return;
		chatService.sendMessage(messageToSend); // Send to AI
		setUserMessage(""); // Clear input
	};

	const handleDownload = (url: string) => {
		window.open(url, "_blank");
	};

	const renderMessageContent = (message: ChatMessage, isLastMessage: boolean) => {
		if (message.role === "user") {
			return <div className="user-message-content">{cleanMessageText(message.content)}</div>;
		}

		if (message.type === "status") {
			return (
				<div className="status-steps-container">
					{message.content
						.split("\n")
						.filter((s) => s.trim() !== "")
						.map((step: any, i) => (
							<div key={i} className="status-step-loading">
								<i className="pi pi-spin pi-spinner"></i>
								<span>{step}</span>
							</div>
						))}
				</div>
			);
		}

		let content = cleanMessageText(message.content) || "";
		const statusPhrases = [
			"Processing your query...",
			"Querying the relational database...",
			"Validating enabled topics for user...",
			"Searching...",
			"Searching through technical documents...",
			"Generating chart...",
		];

		const foundStatuses: string[] = [];
		statusPhrases.forEach((phrase) => {
			if (content.includes(phrase)) {
				foundStatuses.push(phrase);
				content = content.replace(phrase, "").trim();
			}
		});

		// --- MEJORA: Regex para URL sin paréntesis final ---
		let extractedDownloadUrl = message.fileUrl;

		const isButtonLoading = isLastMessage && isAiTyping;

		return (
			<div className="message-text">
				{/* 1. HISTORIAL DE ESTADOS */}
				{foundStatuses.length > 0 && isAiTyping && (
					<div className="status-history-container">
						{foundStatuses.map((status, idx) => (
							<div key={idx} className="status-item-check">
								<div className="spinner-border spinner-border-sm text-primary me-2" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
								<span>{status}</span>
							</div>
						))}
					</div>
				)}

				{/* 2. TEXTO PRINCIPAL (Limpieza total de avisos de descarga) */}
				{content && (
					<div className="main-content-body ">
						<ReactMarkdown
							remarkPlugins={[remarkGfm]}
							components={{
								// This function overrides how 'table' is rendered
								table: ({ children }) => (
									<div className="table-scroll-container">
										<table>{children}</table>
									</div>
								),
							}}>
							{content
								.replace(/Procesando\.{3,}/gi, "")
								.replace(/Analizando\.{3,}/gi, "")
								// Elimina "Download Your File:", "Download CSV File", etc.
								.replace(/(Download\s+(your|the|complete|CSV)\s+(file|data)[:]?)/gi, "")
								// Elimina la frase específica sobre el contenido del CSV
								.replace(/The\s+CSV\s+file\s+contains\s+the\s+complete\s+.*reporting\./gi, "")
								// Elimina cualquier URL de Amazon S3 (dentro o fuera de paréntesis)
								.replace(/\(?https:\/\/.*amazonaws\.com\/[^\s)]*\)?/gi, "")
								// Elimina etiquetas sueltas como "Generating chart" o "CSV File:"
								.replace(/(Generating chart|CSV\s+File:)/gi, "")
								.replace(/\*\*|\[|\]/g, "")
								// This finds "##" and ensures there are two line breaks before it
								.replace(/(##+)/g, "\n\n$1")
								.trim()}
						</ReactMarkdown>
					</div>
				)}

				{/* 3. SECCIÓN DEL GRÁFICO (Explicación + ApexChart) */}
				{message.type === "chart" && message.chartData?.has_chart && (
					<div className="chart-section" style={{ marginTop: "20px" }}>
						<div
							className="chart-container-wrapper"
							style={{
								backgroundColor: "#ffffff",
								borderRadius: "12px",
								padding: "16px",
								border: "1px solid #eef0f2",
							}}>
							<Chart
								options={{
									...message.chartData.chart_configuration.options,
									dataLabels: { enabled: false },
								}}
								series={message.chartData.chart_configuration.series}
								type={message.chartData.chart_type || "bar"}
								height={350}
								width="100%"
							/>
						</div>
					</div>
				)}

				{/* 4. BOTÓN DE DESCARGA AL FINAL DE TODO */}
				{extractedDownloadUrl && (
					<div className="download-area" style={{ marginTop: "24px", borderTop: "1px solid #eee", paddingTop: "16px" }}>
						<p style={{ fontSize: "0.9rem", marginBottom: "8px", fontWeight: "bold" }}>Descarga tus datos:</p>
						<button
							disabled={isButtonLoading}
							className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center w-auto m-auto"
							style={{ height: "40px" }}
							onClick={() => handleDownload(extractedDownloadUrl!)}>
							{isButtonLoading ? (
								<>
									<i className="pi pi-spin pi-spinner"></i>
									{/* Optional: show "Cargando..." or leave empty as per your original logic */}
								</>
							) : (
								<>
									<i className="pi pi-download"></i>
									<span>{"Descargar Archivo"}</span>
								</>
							)}
						</button>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="d-flex flex-column bg-light position-relative vh-100 overflow-hidden ">
			<div className={`${styles["bg-overlay"]}`}></div>
			{/* Main Content */}
			<main className="flex-grow-1 d-flex flex-column align-items-center overflow-hidden">
				<div className="container-md z-1 d-flex flex-column h-100 px-4">
					{/* Hero Section */}
					{chatMessages.length === 0 && (
						<div className="py-3 text-center flex-shrink-0">
							<h1 className="fw-bold display-6 text-dark mb-2">¿Cómo puedo ayudarle hoy?</h1>
							<p className="text-secondary fs-5 mb-0">
								Decisiones inteligentes con chat IA aplicados al Aseguramiento de Ingresos
							</p>
						</div>
					)}

					{/* Chat Area */}
					<div className={` ${styles["chat-container"]}`}>
						{/* 1. Loop through all messages in the state */}
						{chatMessages.map((message, index) => {
							const isAI = message.role === "assistant";

							return (
								<div key={message.id || index} className={`d-flex gap-3 mb-4 ${!isAI ? "flex-row-reverse" : ""}`}>
									{/* Icon Column */}
									<div
										className="rounded-circle d-flex align-items-center justify-content-center shadow-sm p-2"
										style={{ width: "40px", height: "40px", flexShrink: 0, backgroundColor: "#2484ae" }}>
										<img
											className="img-fluid"
											src={isAI ? geminiBotIcon : geminiUserIcon}
											alt={isAI ? "AI" : "User"}
											style={{ filter: "brightness(0) invert(1)", width: "24px", height: "24px" }}
										/>
									</div>

									{/* Content Column */}
									<div
										className={`d-flex flex-column ${styles["bubble-ai-container"]} ${isAI ? "align-items-start" : "align-items-end"}`}>
										<span className="text-uppercase text-muted fw-bold mb-1" style={{ fontSize: "10px", letterSpacing: "1px" }}>
											{isAI ? "Asistente IA" : "Equipo de Aseguramiento de Ingresos"}
										</span>

										<div className={`p-3 ${isAI ? styles["bubble-ai"] : styles["bubble-user"]}`}>
											{isAI && isAiTyping && index === chatMessages.length - 1 && (
												<div className="d-inline-flex align-items-center gap-2 w-100 justify-content-start">
													<div className="spinner-border spinner-border-sm text-primary" role="status">
														<span className="visually-hidden">Loading...</span>
													</div>
													<p className="m-0" style={{ fontSize: "0.9rem", fontStyle: "italic" }}>
														Procesando...
													</p>
												</div>
											)}

											{renderMessageContent(message, index === chatMessages.length - 1)}
										</div>
									</div>
								</div>
							);
						})}

						{/* 2. Show Suggestions ONLY if there are no messages yet (Welcome State) */}
						{chatMessages.length === 0 && (
							<div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
								<button
									className={`btn px-3 py-2 ${styles["btn-suggested"]}`}
									onClick={() => sendMessage("¿Cuál es el consumo promedio de datos por tecnología?")}>
									Consumo promedio de datos por tecnología
								</button>
								<button
									className={`btn px-3 py-2 ${styles["btn-suggested"]}`}
									onClick={() => sendMessage("¿Qué plan tarifario concentra la mayor cantidad de eventos?")}>
									Plan tarifario con la mayor cantidad de eventos
								</button>
							</div>
						)}

						{/* 3. Invisible div to help with auto-scrolling */}
						<div ref={messagesEndRef} />
					</div>
				</div>

				{/* Input Area */}
				<div className="container-md py-2 px-4 flex-shrink-0">
					<div className={`d-flex align-items-center px-3 py-2 row ${styles["input-wrapper"]}`}>
						<textarea
							value={userMessage}
							name="user-input"
							className={`form-control border-0 shadow-none bg-transparent py-2 col-12 ${styles["user-input"]}`}
							placeholder="Preguntame sobre el tráfico de datos de tu red..."
							rows={1} // Starts as one line
							onChange={(e) => {
								setUserMessage(e.target.value);
								// Optional: Auto-expand height logic
								e.target.style.height = "auto";
								e.target.style.height = e.target.scrollHeight + "px";
							}}
							onKeyDown={(e) => {
								// Send on Enter, but allow New Line on Shift+Enter
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault(); // Prevents adding a new line when sending
									if (!isAiTyping) {
										sendMessage();
									}
								}
							}}
						/>
						<div className="d-flex align-items-center justify-content-end gap-2 col-12 pt-1">
							<button
								data-tooltip="Enviar"
								onClick={() => sendMessage()}
								className={`btn text-white rounded-3 p-2 d-flex align-items-center justify-content-center border shadow-sm ${styles["input-btn"]}`}
								style={{ width: "30px", flexShrink: 0 }}
								disabled={isAiTyping || !userMessage.trim()}>
								<img className="material-symbols-outlined img-fluid" src={send} alt="" />
							</button>
							<button
								data-tooltip="Nuevo Chat"
								className={`btn text-white  rounded-3 p-2 d-flex align-items-center justify-content-center border shadow-sm ${styles["input-btn"]}`}
								style={{ width: "30px", flexShrink: 0 }}
								onClick={() => {
									if (window.confirm("¿Estás seguro de que deseas borrar el historial del chat?")) {
										chatService.clearChat();
									}
								}}
								disabled={isAiTyping || !chatMessages.length} // Prevent clearing while AI is responding
							>
								<img className="material-symbols-outlined img-fluid" src={trash} alt="" />
							</button>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
};

export default AiChat;
